import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { CONTACT_ENDPOINT, CONTACT_CATEGORIES } from '../lib/appUrl';
import {
    ArrowRightIcon,
    EnvelopeSimpleIcon,
    UserIcon,
    ChatCircleTextIcon,
    CheckCircleIcon,
} from '@phosphor-icons/react';

/**
 * The only way to reach Portal from this site.
 *
 * Shared by /contact and /support rather than duplicated, because /support is
 * the URL declared on both store listings: a reviewer with no account has to be
 * able to write to us from it, and the two pages drifting apart is exactly how
 * one of them ends up broken without anyone noticing.
 *
 * Posts straight to the Worker, which writes a support_tickets row, emails the
 * sender an acknowledgement with a reference, and nudges the admin inbox.
 */
export default function ContactForm({ defaultCategory = 'other', testIdPrefix = 'contact' }) {
    const [form, setForm] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        category: defaultCategory,
        // Honeypot. Hidden from people, filled by bots.
        company: '',
    });
    const [state, setState] = useState({ submitting: false, reference: null, error: null });

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (state.submitting) return;
        setState({ submitting: true, reference: null, error: null });
        try {
            const res = await fetch(CONTACT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, source: 'marketing' }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setState({ submitting: false, reference: data.reference || '', error: null });
            setForm({
                name: '',
                email: '',
                subject: '',
                message: '',
                category: defaultCategory,
                company: '',
            });
        } catch (err) {
            setState({ submitting: false, reference: null, error: err.message || 'Something went wrong' });
        }
    };

    if (state.reference !== null) {
        return (
            <div
                data-testid={`${testIdPrefix}-success`}
                className="border border-neon/40 bg-[#0A0A0B] neon-shadow p-10 lg:p-14 text-center fade-up"
            >
                <CheckCircleIcon size={56} weight="duotone" className="text-neon mx-auto mb-6" />
                <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-3">// Got it</p>
                <h3 className="font-display uppercase text-4xl sm:text-5xl mb-4 leading-[0.9]">
                    On the desk.
                </h3>
                <p className="text-white/70 max-w-md mx-auto leading-relaxed">
                    Your message is in our admin queue and a confirmation is on its way to your
                    inbox. Expect a reply from a real human, usually within a working day.
                </p>
                {state.reference && (
                    <div className="mt-8 inline-block border border-white/15 px-6 py-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
                            // Your reference
                        </p>
                        <p className="font-mono text-lg text-neon" data-testid={`${testIdPrefix}-reference`}>
                            {state.reference}
                        </p>
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => setState({ submitting: false, reference: null, error: null })}
                    data-testid={`${testIdPrefix}-send-another`}
                    className="mt-8 block mx-auto font-mono text-[10px] uppercase tracking-[0.4em] text-white/60 hover:text-neon underline underline-offset-4 decoration-dotted"
                >
                    Send another
                </button>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            data-testid={`${testIdPrefix}-form`}
            className="border border-white/10 bg-[#0A0A0B] p-8 lg:p-12 space-y-6"
        >
            <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 flex items-center gap-2">
                        <UserIcon size={11} weight="bold" /> Your name
                    </span>
                    <Input
                        type="text"
                        value={form.name}
                        onChange={update('name')}
                        placeholder="Optional"
                        data-testid={`${testIdPrefix}-name`}
                        className="bg-transparent border-white/15 rounded-none h-12 focus-visible:border-neon focus-visible:ring-0 text-white"
                    />
                </label>
                <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 flex items-center gap-2">
                        <EnvelopeSimpleIcon size={11} weight="bold" /> Email
                        <span className="text-neon">*</span>
                    </span>
                    <Input
                        type="email"
                        required
                        value={form.email}
                        onChange={update('email')}
                        placeholder="you@elsewhere.com"
                        data-testid={`${testIdPrefix}-email`}
                        className="bg-transparent border-white/15 rounded-none h-12 focus-visible:border-neon focus-visible:ring-0 text-white"
                    />
                </label>
            </div>

            <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 flex items-center gap-2">
                    What is this about?
                </span>
                <select
                    value={form.category}
                    onChange={update('category')}
                    data-testid={`${testIdPrefix}-category`}
                    className="w-full bg-transparent border border-white/15 rounded-none h-12 px-3 text-white focus:border-neon focus:outline-none"
                >
                    {CONTACT_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value} className="bg-[#0A0A0B]">
                            {c.label}
                        </option>
                    ))}
                </select>
            </label>

            <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 flex items-center gap-2">
                    <ChatCircleTextIcon size={11} weight="bold" /> Subject
                    <span className="text-neon">*</span>
                </span>
                <Input
                    type="text"
                    required
                    maxLength={200}
                    value={form.subject}
                    onChange={update('subject')}
                    placeholder="Press inquiry · partnership · venue · just saying hi…"
                    data-testid={`${testIdPrefix}-subject`}
                    className="bg-transparent border-white/15 rounded-none h-12 focus-visible:border-neon focus-visible:ring-0 text-white"
                />
            </label>

            <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 flex items-center gap-2">
                    Message
                    <span className="text-neon">*</span>
                </span>
                <Textarea
                    required
                    rows={7}
                    maxLength={2000}
                    value={form.message}
                    onChange={update('message')}
                    placeholder="Tell us what you're working on, what room you're trying to fill, what we're missing…"
                    data-testid={`${testIdPrefix}-message`}
                    className="bg-transparent border-white/15 rounded-none focus-visible:border-neon focus-visible:ring-0 text-white resize-none"
                />
            </label>

            {/* Honeypot — hidden from view and from screen readers, unreachable
                by tab. Only an automated form-filler ever puts anything here. */}
            <div className="hidden" aria-hidden="true">
                <label>
                    Company
                    <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.company}
                        onChange={update('company')}
                    />
                </label>
            </div>

            {state.error && (
                <div
                    data-testid={`${testIdPrefix}-error`}
                    className="border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-300"
                >
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-400 mb-1">// Send failed</p>
                    {state.error}
                </div>
            )}

            <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                    Direct to admin · confirmation by email
                </p>
                <Button
                    type="submit"
                    disabled={state.submitting}
                    data-testid={`${testIdPrefix}-submit`}
                    className="bg-neon text-black hover:bg-[#E8FF6B] disabled:opacity-50 rounded-none px-8 py-6 uppercase tracking-[0.2em] text-xs font-bold"
                >
                    {state.submitting ? 'Sending…' : 'Send it'}
                    <ArrowRightIcon size={14} weight="bold" className="ml-3" />
                </Button>
            </div>
        </form>
    );
}
